import { useState } from 'react';
import { Badge } from '@design-system/Badge';
import { Image } from '@design-system/Image';
import { Link } from '@design-system/Link';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { InfoIcon } from '@/components/icons/InfoIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@design-system/Dialog';
import { SMPDialogContent } from '../components';
import { cn } from '@/utils/styles';
import type { SMPSubscriptionDetail, SMPPlanComponent, DiscountsResponse } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  getStatusBadgeClassName,
  getStatusDisplayLabel,
  formatPriceFromCents,
  formatSMPDate,
  isSubscriptionPaused,
  getComponentSupplyLabel,
  formatProductName,
  getProductGradientClass,
  getProductImageForSMP,
} from '../utils/smpHelpers';
import { SMP_DISCOUNT_CODE_PATH } from '@/constants/paths';

const COLLAPSED_PRODUCT_COUNT = 2;

interface SMPLandingPageHeaderProps {
  subscription: SMPSubscriptionDetail;
  productComponents: SMPPlanComponent[];
  customerDiscount: DiscountsResponse | null;
}

const renderProductImage = (title: string, image: string) => {
  return getProductImageForSMP(title).length > 0 ? getProductImageForSMP(title) : image;
};

const ProductComponentRow = ({ component }: { component: SMPPlanComponent }) => (
  <div
    className="flex items-center gap-5"
    data-testid={`smp-header-productItem-${component.product_id}`}
  >
    <div
      className={cn(
        "relative size-[72px] rounded-[12px] overflow-hidden flex items-center justify-center shrink-0",
        getProductGradientClass(component.title),
      )}
    >
      <Image
        src={renderProductImage(component.title, component.image)}
        alt={component.title}
        className="object-contain size-10"
      />
    </div>
    <div className="flex flex-col gap-2">
      <span
        className="typography-body text-secondary"
        data-testid={`smp-header-productName-${component.product_id}`}
      >
        {formatProductName(component.title)}
      </span>
      <span
        className="typography-caption2 text-secondary"
        data-testid={`smp-header-productSupply-${component.product_id}`}
      >
        {getComponentSupplyLabel(component)}
      </span>
    </div>
  </div>
);

export const SMPLandingPageHeader = ({
  subscription,
  productComponents,
  customerDiscount,
}: SMPLandingPageHeaderProps) => {
  const { line_items, subscription_status, resume_date } = subscription;
  const [isExpanded, setIsExpanded] = useState(false);

  const totalPriceCents = line_items.reduce(
    (sum, item) => sum + item.price,
    0,
  );

  const hasDiscount = !!customerDiscount;
  const discountValue = hasDiscount ? parseFloat(customerDiscount.discountValue) : 0;
  const isPercentage = customerDiscount?.discountType === 'percentage';

  const discountedPriceCents = hasDiscount
    ? isPercentage
      ? Math.round(totalPriceCents * (1 - discountValue / 100))
      : totalPriceCents - discountValue * 100
    : totalPriceCents;

  const savingsLabel = hasDiscount
    ? isPercentage
      ? `${Math.round(discountValue)}% Saving`
      : `$${discountValue.toFixed(0)} Saving`
    : '';

  const planName =
    line_items.length > 0 ? line_items[0].product_name : 'Subscription Plan';

  const isPaused = isSubscriptionPaused(subscription_status);

  const hasComponents = productComponents.length > 0;
  const isCollapsible = hasComponents && productComponents.length > COLLAPSED_PRODUCT_COUNT;
  const visibleComponents = isCollapsible && !isExpanded
    ? productComponents.slice(0, COLLAPSED_PRODUCT_COUNT)
    : productComponents;

  return (
    <Card data-testid="smp-header-card">
      <CardHeader data-testid="smp-header-cardHeader">
        <CardTitle
          className="flex flex-row items-center justify-between typography-h2 text-primary"
          data-testid="smp-header-cardTitle"
        >
          <span>{planName}</span>
          <Badge
            size="sm"
            className={cn(
              'capitalize',
              getStatusBadgeClassName(subscription_status),
            )}
            data-testid="smp-header-statusBadge"
          >
            {getStatusDisplayLabel(subscription_status)}
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent data-testid="smp-header-cardContent">
        <div className="flex flex-col gap-6">
          {/* Resume date info when paused — shown at top of card content */}
          {isPaused && resume_date && (
            <div
              className="flex items-center gap-1"
              data-testid="smp-header-resumeDate"
            >
              <span className="typography-headline3 text-primary">
                Your subscription will resume on {formatSMPDate(resume_date)}.
              </span>
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className="inline-flex items-center justify-center size-5 rounded-full text-tertiary hover:text-secondary transition-colors"
                    data-testid="smp-header-resumeDateInfo"
                    aria-label="Resume date information"
                  >
                    <InfoIcon className="size-4" />
                  </button>
                </DialogTrigger>
                <SMPDialogContent>
                  <DialogHeader className="gap-6 text-start">
                    <DialogTitle className="typography-h3 text-primary">
                      Resume Date
                    </DialogTitle>
                    <DialogDescription className="typography-body text-secondary">
                      This is the date your subscription will resume and you
                      will be charged for your next order. You can change the
                      resume date under &ldquo;<span className="text-success">Manage Subscription</span>&rdquo;.
                    </DialogDescription>
                  </DialogHeader>
                </SMPDialogContent>
              </Dialog>
            </div>
          )}

          {/* Price at top when no discount */}
          {!hasDiscount && (
            <div
              className="flex items-end gap-0.5"
              data-testid="smp-header-price"
            >
              <span className="typography-body text-primary">
                {formatPriceFromCents(totalPriceCents)}
              </span>
              <span className="typography-caption1 text-primary">
                / month + tax
              </span>
            </div>
          )}

          {/* Individual product list */}
          {hasComponents && (
            <div
              className="flex flex-col gap-2"
              data-testid="smp-header-productList"
            >
              <div className="flex flex-col gap-4">
                {visibleComponents.map((component) => (
                  <ProductComponentRow key={component.product_id} component={component} />
                ))}
              </div>

              {isCollapsible && (
                <button
                  onClick={() => setIsExpanded((prev) => !prev)}
                  className="flex items-center justify-center gap-1 py-2 typography-body text-primary cursor-pointer"
                  data-testid="smp-header-toggleProducts"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                  <ChevronRightIcon
                    className={cn(
                      'size-6 transition-transform',
                      isExpanded ? '-rotate-90' : 'rotate-90',
                    )}
                  />
                </button>
              )}
            </div>
          )}

          {/* Price at bottom when discount is active */}
          {hasDiscount && (
            <div
              className="flex flex-wrap items-center gap-1.5"
              data-testid="smp-header-discountedPrice"
            >
              <span className="typography-body text-tertiary line-through decoration-error">
                {formatPriceFromCents(totalPriceCents)}
              </span>
              <span className="typography-body text-primary">
                {formatPriceFromCents(discountedPriceCents)}
              </span>
              <span className="typography-caption1 text-primary">
                / month + tax
              </span>
              {savingsLabel && (
                <Badge
                  size="sm"
                  className="border border-success/20 bg-transparent text-success"
                  data-testid="smp-header-savingsBadge"
                >
                  {savingsLabel}
                </Badge>
              )}
              <Link
                to={SMP_DISCOUNT_CODE_PATH}
                className="inline-flex items-center gap-0 typography-caption1 text-success no-underline"
                data-testid="smp-header-seeDetail"
              >
                See detail
                <ChevronRightIcon className="size-6 text-success" />
              </Link>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
